import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import { toast } from "react-toastify";
import apiClient from "../../../Utils/apiClient";

const MessageForm = ({ open, handleClose, message, refresh }) => {
  const [formData, setFormData] = useState({
    message: "",
    messageType: "text",
    targetAudience: ["driver"],
    escortType: ["driver-escort"],
    intervalSeconds: 300,
    order: 0,
    isActive: true,
  });
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (message) {
      setFormData(message);
      if (message.messageType === "audio" && message.audioUrl) {
        setAudioPreviewUrl(message.audioUrl);
      }
    }
  }, [message]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleTypeChange = (event, newType) => {
    if (newType !== null) {
      setFormData((prev) => ({
        ...prev,
        messageType: newType,
      }));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ["audio/mpeg", "audio/wav", "audio/mp4", "audio/aac", "audio/ogg", "audio/x-m4a", "audio/webm"];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Only MP3, WAV, M4A, AAC, OGG audio files are allowed");
        return;
      }
      setAudioFile(file);
      setAudioPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleRemoveAudio = () => {
    setAudioFile(null);
    setAudioPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    if (formData.messageType === "text" && !formData.message.trim()) {
      toast.error("Message text is required");
      return;
    }

    if (formData.messageType === "audio" && !audioFile && !audioPreviewUrl) {
      toast.error("Please upload an audio file");
      return;
    }

    try {
      const submitData = new FormData();
      submitData.append("message", formData.message || "");
      submitData.append("messageType", formData.messageType);
      submitData.append("targetAudience", JSON.stringify(formData.targetAudience));
      submitData.append("escortType", JSON.stringify(formData.escortType));
      submitData.append("intervalSeconds", formData.intervalSeconds);
      submitData.append("order", formData.order);
      submitData.append("isActive", formData.isActive);

      if (audioFile) {
        submitData.append("audioFile", audioFile);
      }

      if (message && message._id) {
        await apiClient.put(`/voice-deterrence/admin/messages/${message._id}`, submitData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Message updated successfully");
      } else {
        await apiClient.post("/voice-deterrence/admin/messages", submitData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Message created successfully");
      }
      refresh();
      handleClose();
    } catch (error) {
      toast.error("Failed to save message");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{message ? "Edit Message" : "Add New Message"}</DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>

        {/* Message Type Toggle */}
        <Box sx={{ mb: 3, mt: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
            Message Type
          </Typography>
          <ToggleButtonGroup
            value={formData.messageType}
            exclusive
            onChange={handleTypeChange}
            fullWidth
            size="small"
          >
            <ToggleButton value="text" sx={{ fontWeight: "bold" }}>
              📝 Text (TTS)
            </ToggleButton>
            <ToggleButton value="audio" sx={{ fontWeight: "bold" }}>
              🔊 Audio File
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Text Message Input */}
        {formData.messageType === "text" && (
          <TextField
            autoFocus
            name="message"
            label="Message Text (To be read aloud) *"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={formData.message}
            onChange={handleChange}
            sx={{ mb: 3 }}
          />
        )}

        {/* Audio File Upload */}
        {formData.messageType === "audio" && (
          <Box sx={{ mb: 3 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp3,.wav,.m4a,.aac,.ogg"
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />

            {!audioPreviewUrl ? (
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: "2px dashed #ccc",
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  cursor: "pointer",
                  "&:hover": { borderColor: "#1A73E8", bgcolor: "#f5f9ff" },
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 40, color: "#9e9e9e", mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Click to upload audio file
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  MP3, WAV, M4A, AAC, OGG
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <AudioFileIcon sx={{ fontSize: 36, color: "#1A73E8" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {audioFile ? audioFile.name : "Current Audio"}
                  </Typography>
                  <audio controls src={audioPreviewUrl} style={{ width: "100%", marginTop: 8 }} />
                </Box>
                <IconButton onClick={handleRemoveAudio} color="error" size="small">
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}

            <TextField
              name="message"
              label="Optional Label / Description"
              type="text"
              fullWidth
              value={formData.message}
              onChange={handleChange}
              sx={{ mt: 2 }}
              size="small"
            />
          </Box>
        )}

        <TextField
          name="intervalSeconds"
          label="Interval Before Next Message (Seconds)"
          type="number"
          fullWidth
          value={formData.intervalSeconds}
          onChange={handleChange}
          sx={{ mb: 3 }}
        />

        <TextField
          name="order"
          label="Playback Order (e.g. 1, 2, 3)"
          type="number"
          fullWidth
          value={formData.order}
          onChange={handleChange}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.isActive}
              onChange={handleCheckboxChange}
              name="isActive"
              color="primary"
            />
          }
          label="Is Active"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} sx={{ color: "text.secondary", mr: 1, fontWeight: "bold" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{ backgroundColor: "#1A73E8", color: "#fff", fontWeight: "bold", "&:hover": { backgroundColor: "#1557B0" } }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MessageForm;
